import type { QueryClient } from "@tanstack/react-query";
import { resetWalletSetupLock } from "@/lib/wallet/walletSetupLock";
import { endServerSession } from "./auth";
import { queryKeys } from "@/lib/query/queryKeys";

export type CompleteSignOutOptions = {
  queryClient: QueryClient;
  invalidateRouter: () => Promise<void>;
  navigateToLogin: () => Promise<void>;
};

function clearAuthQueries(queryClient: QueryClient): void {
  queryClient.setQueryData(queryKeys.users.me(), null);
  queryClient.removeQueries({
    predicate: (query) => {
      const [root, segment] = query.queryKey;
      return !(root === "users" && segment === "me");
    },
  });
}

/**
 * End session → drop identity → /login → clear query cache.
 */
export async function completeSignOut({
  queryClient,
  invalidateRouter,
  navigateToLogin,
}: CompleteSignOutOptions): Promise<void> {
  resetWalletSetupLock();
  await endServerSession();

  queryClient.setQueryData(queryKeys.users.me(), null);

  let leftForLogin = false;
  try {
    await navigateToLogin();
    leftForLogin = true;
  } catch {
    // SPA navigate failed — hard fallback after local cleanup
  }

  clearAuthQueries(queryClient);

  try {
    await invalidateRouter();
  } catch {
    // already leaving the app
  }

  if (!leftForLogin) {
    window.location.replace("/login");
  }
}
